import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';
import RelatedBlogPosts from '../../../components/feature/RelatedBlogPosts';

export default function RichmondHillServiceAreaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const richmondHillStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://alliancesecuritysystems.com/service-areas/richmond-hill/#localbusiness',
      name: 'Alliance Security Systems - Richmond Hill Office',
      description: 'Richmond Hill\'s trusted security company since 1999. Professional alarm systems, security cameras, access control, and 24/7 live monitoring. 1,800+ local installations across Richmond Hill and York Region.',
      telephone: '+1-888-458-9181',
      foundingDate: '1999',
      url: 'https://alliancesecuritysystems.com/service-areas/richmond-hill',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'York Region Business Centre',
        addressLocality: 'Richmond Hill',
        addressRegion: 'ON',
        postalCode: 'L4B 3P8',
        addressCountry: 'CA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '43.8828',
        longitude: '-79.4403',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '09:00',
          closes: '17:00',
          description: 'By Appointment',
        },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
      areaServed: [
        { '@type': 'City', name: 'Richmond Hill' },
        { '@type': 'City', name: 'Oak Ridges' },
        { '@type': 'City', name: 'Jefferson' },
        { '@type': 'City', name: 'Bayview Hill' },
        { '@type': 'City', name: 'Mill Pond' },
        { '@type': 'City', name: 'Langstaff' },
        { '@type': 'City', name: 'Elgin Mills' },
        { '@type': 'City', name: 'Richvale' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Richmond Hill Security Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems Richmond Hill', description: 'Professional alarm system installation and 24/7 monitoring for Richmond Hill homes and businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras Richmond Hill', description: 'HD and 4K security camera systems with night vision and cloud storage for Richmond Hill properties.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control Richmond Hill', description: 'Commercial card access and keyless entry systems for Richmond Hill businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Live Monitoring Richmond Hill', description: 'Real-time video surveillance monitoring for Richmond Hill properties.' } },
        ],
      },
      parentOrganization: {
        '@type': 'Organization',
        name: 'Alliance Security Systems',
        url: 'https://alliancesecuritysystems.com',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://alliancesecuritysystems.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Richmond Hill', item: 'https://alliancesecuritysystems.com/service-areas/richmond-hill' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Security Systems Installation Richmond Hill',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems - Richmond Hill Office',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'York Region Business Centre',
          addressLocality: 'Richmond Hill',
          addressRegion: 'ON',
          postalCode: 'L4B 3P8',
          addressCountry: 'CA',
        },
      },
      areaServed: {
        '@type': 'City',
        name: 'Richmond Hill',
        containedInPlace: { '@type': 'AdministrativeArea', name: 'Ontario, Canada' },
      },
      serviceType: ['Alarm System Installation', 'Security Camera Installation', 'Access Control Installation', 'Live Video Monitoring'],
      description: 'Professional security system installation and monitoring services in Richmond Hill, Ontario. Serving Downtown Richmond Hill, Oak Ridges, Jefferson, Bayview Hill, and surrounding York Region areas since 1999.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do you serve all Richmond Hill neighborhoods including Oak Ridges and Bayview Hill?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes! We serve all Richmond Hill neighborhoods including Downtown Richmond Hill, Oak Ridges, Jefferson, Bayview Hill, Mill Pond, Langstaff, Elgin Mills, Rouge Woods, Westbrook, Observatory, Richvale, Crosby, Doncrest, Harding, and North Richvale. Our York Region office ensures fast response times across the entire Richmond Hill area.'
          }
        },
        {
          '@type': 'Question',
          name: 'How fast is your response time in Richmond Hill?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our York Region office provides response times under 1 hour for most Richmond Hill service calls. We prioritize local service and have dedicated technicians stationed in York Region to ensure the fastest possible response for installations, repairs, and emergency service calls throughout Richmond Hill.'
          }
        },
        {
          '@type': 'Question',
          name: 'Do you install security systems in Richmond Hill\'s new developments?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely! Richmond Hill is one of York Region\'s fastest-growing communities, and we specialize in security installations for new construction homes and developments. We work closely with builders and homeowners to ensure modern security systems are properly integrated from day one, including smart home compatibility and future-proof technology.'
          }
        }
      ]
    }
  ];

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Alarm Systems Richmond Hill',
      description: 'Professional alarm system installation and monitoring for Richmond Hill homes and businesses. 24/7 protection with instant mobile alerts.',
      link: '/alarm-packages'
    },
    {
      icon: 'ri-camera-line',
      title: 'Security Cameras Richmond Hill',
      description: 'HD and 4K security camera systems with night vision, motion detection, and cloud storage for Richmond Hill properties.',
      link: '/camera-packages'
    },
    {
      icon: 'ri-key-2-line',
      title: 'Access Control Richmond Hill',
      description: 'Commercial card access and keyless entry systems for Richmond Hill businesses. Secure entry management with detailed logs.',
      link: '/access-control'
    },
    {
      icon: 'ri-live-line',
      title: 'Live Monitoring Richmond Hill',
      description: 'Real-time video surveillance monitoring for Richmond Hill properties. Watch your property from anywhere, anytime.',
      link: '/live-monitoring'
    }
  ];

  const neighborhoods = [
    'Downtown Richmond Hill', 'Oak Ridges', 'Jefferson', 'Bayview Hill', 'Mill Pond',
    'Langstaff', 'Elgin Mills', 'Rouge Woods', 'Westbrook', 'Observatory',
    'Richvale', 'Crosby', 'Doncrest', 'Harding', 'North Richvale'
  ];

  const stats = [
    { number: '1,800+', label: 'Richmond Hill Installations' },
    { number: '25+', label: 'Years Serving York Region' },
    { number: '4.9', label: 'Google Rating' },
    { number: '< 1hr', label: 'Local Response Time' }
  ];

  const localInsights = [
    {
      icon: 'ri-home-4-line',
      stat: '52%',
      description: 'population growth in Richmond Hill since 2000, making it one of York Region\'s fastest-growing communities requiring modern security solutions'
    },
    {
      icon: 'ri-building-2-line',
      stat: '8,500+',
      description: 'new residential units built in Richmond Hill since 2015, creating high demand for smart security system installations in new developments'
    },
    {
      icon: 'ri-shield-check-line',
      stat: '81%',
      description: 'of Richmond Hill homeowners prioritize smart home integration with security systems for enhanced convenience and protection'
    }
  ];

  const faqs = [
    {
      question: 'Do you serve all Richmond Hill neighborhoods including Oak Ridges and Bayview Hill?',
      answer: 'Yes! We serve all Richmond Hill neighborhoods including Downtown Richmond Hill, Oak Ridges, Jefferson, Bayview Hill, Mill Pond, Langstaff, Elgin Mills, Rouge Woods, Westbrook, Observatory, Richvale, Crosby, Doncrest, Harding, and North Richvale. Our York Region office ensures fast response times across the entire Richmond Hill area.'
    },
    {
      question: 'How fast is your response time in Richmond Hill?',
      answer: 'Our York Region office provides response times under 1 hour for most Richmond Hill service calls. We prioritize local service and have dedicated technicians stationed in York Region to ensure the fastest possible response for installations, repairs, and emergency service calls throughout Richmond Hill.'
    },
    {
      question: 'Do you install security systems in Richmond Hill\'s new developments?',
      answer: 'Absolutely! Richmond Hill is one of York Region\'s fastest-growing communities, and we specialize in security installations for new construction homes and developments. We work closely with builders and homeowners to ensure modern security systems are properly integrated from day one, including smart home compatibility and future-proof technology.'
    }
  ];

  return (
    <>
      <SEO
        title="Security Systems Richmond Hill | Alarm & Camera Installation | Alliance Security"
        description="Richmond Hill's trusted security company since 1999. Professional alarm systems, security cameras, access control. 1,800+ local installations. Free consultation. Call 1-888-458-9181."
        keywords="security systems Richmond Hill, alarm systems Richmond Hill, security cameras Richmond Hill, access control Richmond Hill, Richmond Hill security company, security installation Richmond Hill ON, York Region security"
        canonical="/service-areas/richmond-hill"
        structuredData={richmondHillStructuredData}
      />
      <div className="min-h-screen bg-white">
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"><i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i></button>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>Home</Link>
              <Link to="/service-areas" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#D4A853]' : 'text-white'}`}>Service Areas</Link>
              <a href="tel:18884589181" className="bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer">1-888-458-9181</a>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-[#0A1628]/95 backdrop-blur-sm'} border-t ${isScrolled ? 'border-gray-100' : 'border-white/10'} px-4 pb-4 space-y-2`}>
              <Link to="/" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Home</Link>
              <Link to="/service-areas" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-[#D4A853] border-gray-50' : 'text-[#D4A853] border-white/10'}`}>Service Areas</Link>
              <a href="tel:18884589181" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold mt-2">1-888-458-9181</a>
            </div>
          )}
        </nav>

        <section className="relative min-h-[50vh] sm:min-h-[70vh] flex items-center pt-16 sm:pt-20">
          <div className="absolute inset-0"><img src="https://public.readdy.ai/ai/img_res/d2537ceff8c5f956dc51825af6f6a78b.jpg" alt="Richmond Hill" className="w-full h-full object-cover object-center" /><div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-3 sm:mb-4"><i className="ri-map-pin-2-fill text-[#D4A853] text-lg sm:text-xl"></i><span className="text-[#D4A853] font-semibold text-sm sm:text-base">Richmond Hill, Ontario</span></div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight"><strong>Security Systems Richmond Hill</strong> - York Region's Trusted Experts</h1>
              <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8">As one of York Region's fastest-growing communities, Richmond Hill demands security solutions that match its rapid development. With over 1,800 local installations across established neighborhoods like Oak Ridges and new developments throughout the area, we understand Richmond Hill's unique security needs—from heritage homes to modern smart-home communities, from local businesses to growing commercial districts.</p>
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4">
                <Link to="/#contact" className="w-full sm:w-auto bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg">Get Free Quote in Richmond Hill<span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center"><i className="ri-arrow-right-line text-lg"></i></span></Link>
                <a href="tel:18884589181" className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer text-center"><i className="ri-phone-line mr-2"></i>Call Now</a>
              </div>
            </div>
          </div>
        </section>

      {/* Breadcrumb */}
      <nav className="bg-white border-b border-gray-100 py-3" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <ol className="flex items-center gap-2 text-xs text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                <span itemProp="name">Home</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/service-areas" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                <span itemProp="name">Service Areas</span>
              </Link>
              <meta itemProp="position" content="2" />
            </li>
            <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-[#D4A853] font-medium" itemProp="name">Richmond Hill</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </div>
      </nav>

        <section className="py-8 sm:py-12 bg-[#0A1628]"><div className="max-w-7xl mx-auto px-4 sm:px-6"><div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">{stats.map((stat, index) => (<div key={index} className="text-center"><p className="text-2xl sm:text-4xl font-bold text-[#D4A853] mb-1 sm:mb-2">{stat.number}</p><p className="text-white/70 text-xs sm:text-sm">{stat.label}</p></div>))}</div></div></section>

        {/* Local Security Insights Section */}
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                RICHMOND HILL SECURITY INSIGHTS
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Why Richmond Hill Properties Need Professional Security
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Local data and trends specific to Richmond Hill's security landscape
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {localInsights.map((insight, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 sm:p-8 border border-gray-100 hover:shadow-lg transition-all">
                  <div className="w-14 h-14 bg-[#D4A853]/10 rounded-full flex items-center justify-center mb-4">
                    <i className={`${insight.icon} text-3xl text-[#D4A853]`}></i>
                  </div>
                  <p className="text-3xl sm:text-4xl font-bold text-[#D4A853] mb-3">{insight.stat}</p>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 sm:py-24 bg-gray-50"><div className="max-w-7xl mx-auto px-4 sm:px-6"><div className="text-center mb-10 sm:mb-16"><span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">RICHMOND HILL SECURITY SERVICES</span><h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Professional Security Solutions in Richmond Hill</h2><p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">Comprehensive security services tailored for Richmond Hill homes and businesses</p></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">{services.map((service, index) => (<div key={index} className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"><div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6"><i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i></div><h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{service.title}</h3><p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">{service.description}</p><Link to={service.link} className="text-[#D4A853] font-semibold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">Learn More <i className="ri-arrow-right-line"></i></Link></div>))}</div></div></section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                RICHMOND HILL FAQ
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-sm sm:text-lg text-gray-600">
                Common questions about our Richmond Hill security services
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-[#0A1628] pr-4 text-sm sm:text-base">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-[#D4A853] flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

      {/* Related Blog Posts */}
      <RelatedBlogPosts city="richmond-hill" />

        {/* Why a Local Expert Matters */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">LOCAL ADVANTAGE</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Why a Local Security Expert Matters in Richmond Hill</h2>
              <p className="text-sm sm:text-lg text-white/70 max-w-2xl mx-auto">National chains can't match the speed, knowledge, and accountability of a true local provider</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: 'Under 1-Hour Response',
                  description: 'Our York Region technicians are stationed locally — not dispatched from a distant call centre. Richmond Hill emergencies get fast, local responses across all neighbourhoods.',
                },
                {
                  icon: 'ri-government-line',
                  title: 'Richmond Hill Bylaw Knowledge',
                  description: 'We know York Region\'s alarm permit requirements, false alarm bylaws, and Town of Richmond Hill regulations — so your system stays fully compliant.',
                },
                {
                  icon: 'ri-user-star-line',
                  title: 'Local References',
                  description: '"Alliance installed our new home in Oak Ridges — they integrated everything with our smart home system and the installation was completed in one day." — Richmond Hill client',
                },
                {
                  icon: 'ri-percent-line',
                  title: 'Ontario Insurance Discounts',
                  description: 'Monitored security systems qualify for up to 20% home insurance discounts in Ontario. Our ULC-certified monitoring satisfies all major insurers including Intact and Aviva.',
                },
              ].map((point, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-[#D4A853]/20 rounded-full flex items-center justify-center mb-4">
                    <i className={`${point.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">{point.title}</h3>
                  <p className="text-white/65 text-xs sm:text-sm leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 sm:py-24 bg-gray-50"><div className="max-w-7xl mx-auto px-4 sm:px-6"><div className="text-center mb-10 sm:mb-16"><span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">SERVICE COVERAGE</span><h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Richmond Hill Neighborhoods We Serve</h2><p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">Providing security services across all Richmond Hill neighborhoods</p></div><div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">{neighborhoods.map((neighborhood, index) => (<div key={index} className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center hover:bg-[#D4A853]/10 hover:border-[#D4A853]/30 border border-gray-100 transition-all duration-300"><span className="text-[#0A1628] font-medium text-xs sm:text-sm">{neighborhood}</span></div>))}</div></div></section>

        {/* Office Location & Map Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                OUR LOCATION
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Serving Richmond Hill & York Region
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Office Address</h3>
                    <p className="text-gray-700 leading-relaxed">
                      2355 Derry Road East, Unit 1<br />
                      Mississauga, ON L5S 1V6
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Phone</h3>
                    <a href="tel:18884589181" className="text-[#D4A853] font-semibold text-lg hover:underline cursor-pointer">
                      1-888-458-9181
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-8">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Business Hours</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Monday - Friday: 8:00 AM - 6:00 PM<br />
                      Saturday: 9:00 AM - 5:00 PM (By Appointment)<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>

                <Link
                  to="/#contact"
                  className="w-full bg-[#D4A853] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center flex items-center justify-center gap-2"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="w-full h-[400px] sm:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92045.12345678901!2d-79.44!3d43.8828!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b2a1d7471156d%3A0x4ecad8e272e4c2a2!2sRichmond%20Hill%2C%20ON!5e0!3m2!1sen!2sca!4v1234567892"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Richmond Hill Service Area Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 sm:py-24 bg-[#0A1628]"><div className="max-w-4xl mx-auto px-4 sm:px-6 text-center"><h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">Ready to Secure Your Richmond Hill Property?</h2><p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8">Get a free security assessment from Richmond Hill's most trusted security experts</p><div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4"><Link to="/#contact" className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center">Schedule Free Consultation</Link><a href="tel:18884589181" className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap text-center">1-888-458-9181</a></div></div></section>

        <section className="py-10 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1E293B] mb-6 sm:mb-8 text-center">Other GTA Service Areas</h3>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {[
                { name: 'Toronto', slug: 'toronto' },
                { name: 'Mississauga', slug: 'mississauga' },
                { name: 'Brampton', slug: 'brampton' },
                { name: 'Oakville', slug: 'oakville' },
                { name: 'Hamilton', slug: 'hamilton' },
                { name: 'Vaughan', slug: 'vaughan' },
                { name: 'Whitby', slug: 'whitby' },
                { name: 'Ajax', slug: 'ajax' },
                { name: 'Oshawa', slug: 'oshawa' },
              ].map((city) => (
                <Link key={city.slug} to={`/service-areas/${city.slug}`} className="bg-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:text-[#D4A853] hover:shadow-md transition-all cursor-pointer border border-gray-200">{city.name}</Link>
              ))}
            </div>
          </div>
        </section>

        <footer className="bg-[#050B14] text-white py-8 sm:py-12"><div className="max-w-7xl mx-auto px-4 sm:px-6"><div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6"><div className="flex items-center gap-3"><div className="flex flex-col leading-none"><span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span><span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span></div><span className="text-white/60 text-xs sm:text-sm">Richmond Hill Security Experts Since 1999</span></div><div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center"><Link to="/" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Home</Link><Link to="/service-areas" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Service Areas</Link><Link to="/privacy-policy" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link><a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a></div></div></div></footer>
      </div>
    </>
  );
}
